import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { ChevronRight, ChevronDown, Inbox, CheckCircle2, Star, Plus, Folder, Sparkles, Trash2, Pencil } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { invoiceFoldersAPI } from '@shared/apiClient';
import { getErrorMessage } from '@shared/utils';
import { MAILBOXES } from './invoicingConstants';

const SYSTEM_ICONS = {
  inbox: Inbox,
  completed: CheckCircle2,
  starred: Star
};

function InboxBadge({ folderId }) {
  const { data } = useQuery(
    ['invoiceFolderCount', folderId],
    () => invoiceFoldersAPI.getFolderServiceCalls(folderId, { limit: 1 }).then((res) => res.data.pagination.total),
    { enabled: !!folderId, refetchInterval: 60000 }
  );
  if (!data) return null;
  return <span className="text-2xs tabular-nums font-semibold text-gray-500">{data}</span>;
}

function MailboxSection({
  mailbox,
  isActive,
  isExpanded,
  onToggleExpand,
  onSelectMailbox,
  activeFolderId,
  onSelectFolder,
  onRequestCreateFolder,
  onRequestEditFolder,
  onRequestDeleteFolder
}) {
  const { data: folders = [], isLoading } = useQuery(
    ['invoiceFolders', mailbox.id],
    () => invoiceFoldersAPI.getFolders(mailbox.id).then((res) => res.data.folders),
    { enabled: isExpanded }
  );

  const inboxFolder = folders.find((f) => f.systemKey === 'inbox');

  return (
    <div className="border-b border-surface-border">
      <button
        type="button"
        onClick={() => {
          onToggleExpand(mailbox.id);
          onSelectMailbox(mailbox.id);
        }}
        className={clsx(
          'w-full flex items-center gap-1.5 px-3 py-2.5 text-left transition-colors',
          isActive ? 'bg-primary-50' : 'hover:bg-surface-hover'
        )}
        data-tour-id={`invoicing-mailbox-${mailbox.id}`}
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
        <span className={clsx('text-sm font-semibold flex-1 truncate', isActive ? 'text-primary-700' : 'text-gray-800')}>
          {mailbox.label}
        </span>
        {inboxFolder && <InboxBadge folderId={inboxFolder.id} />}
      </button>

      {isExpanded && (
        <div className="pb-1.5">
          {isLoading ? (
            <div className="px-4 py-2 text-2xs text-gray-400">Loading folders...</div>
          ) : (
            <ul>
              {folders.map((folder) => {
                const Icon = folder.kind === 'system' ? (SYSTEM_ICONS[folder.systemKey] || Folder) : (folder.kind === 'smart' ? Sparkles : Folder);
                const active = folder.id === activeFolderId;
                return (
                  <li key={folder.id} className="group">
                    <button
                      type="button"
                      onClick={() => onSelectFolder(mailbox.id, folder)}
                      className={clsx(
                        'w-full flex items-center gap-2 pl-8 pr-2 py-1.5 text-left transition-colors',
                        active ? 'bg-primary-100 border-l-2 border-l-primary-600 pl-[30px]' : 'hover:bg-surface-hover'
                      )}
                      data-tour-id={folder.systemKey ? `invoicing-folder-${folder.systemKey}` : undefined}
                    >
                      <Icon className={clsx('h-3.5 w-3.5 flex-shrink-0', active ? 'text-primary-700' : 'text-gray-400')} />
                      <span className={clsx('text-xs flex-1 truncate', active ? 'font-semibold text-primary-800' : 'text-gray-700')}>
                        {folder.name}
                      </span>
                      {folder.kind !== 'system' && folder.scope && folder.scope !== 'active' && (
                        <span
                          className="text-2xs text-gray-400 flex-shrink-0"
                          title={folder.scope === 'archive' ? 'Archive: only shows finalized items' : 'All: shows items regardless of finalized status'}
                        >
                          {folder.scope === 'archive' ? 'archive' : 'all'}
                        </span>
                      )}
                      {folder.kind !== 'system' && (
                        <span className="opacity-0 group-hover:opacity-100 flex items-center gap-1 flex-shrink-0">
                          <span
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestEditFolder(folder);
                            }}
                            className="text-gray-400 hover:text-primary-600"
                            title="Edit folder"
                          >
                            <Pencil className="h-3 w-3" />
                          </span>
                          <span
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestDeleteFolder(folder);
                            }}
                            className="text-gray-400 hover:text-red-500"
                            title="Delete folder"
                          >
                            <Trash2 className="h-3 w-3" />
                          </span>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => onRequestCreateFolder(mailbox.id)}
            className="w-full flex items-center gap-1.5 pl-8 pr-2 py-1.5 text-2xs text-gray-500 hover:text-primary-700 hover:bg-surface-hover transition-colors"
            data-tour-id={`invoicing-add-folder-${mailbox.id}`}
          >
            <Plus className="h-3 w-3" />
            <span>Add folder</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function MailboxSidebar({
  activeMailbox,
  activeFolderId,
  onSelectMailbox,
  onSelectFolder,
  onRequestCreateFolder,
  onRequestEditFolder
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(activeMailbox);

  const handleToggleExpand = (mailboxId) => {
    setExpanded((current) => (current === mailboxId ? current : mailboxId));
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Items inside won't be deleted.`)) return;
    try {
      await invoiceFoldersAPI.deleteFolder(folder.id);
      queryClient.invalidateQueries(['invoiceFolders', folder.mailbox]);
      toast.success('Folder deleted');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="w-64 flex-shrink-0 border-r border-surface-border bg-surface flex flex-col overflow-y-auto" data-tour-id="invoicing-sidebar">
      <div className="hub-dock-header">
        <span className="hub-dock-title">Invoicing</span>
      </div>
      {MAILBOXES.map((mailbox) => (
        <MailboxSection
          key={mailbox.id}
          mailbox={mailbox}
          isActive={activeMailbox === mailbox.id}
          isExpanded={expanded === mailbox.id}
          onToggleExpand={handleToggleExpand}
          onSelectMailbox={onSelectMailbox}
          activeFolderId={activeFolderId}
          onSelectFolder={onSelectFolder}
          onRequestCreateFolder={onRequestCreateFolder}
          onRequestEditFolder={onRequestEditFolder}
          onRequestDeleteFolder={handleDeleteFolder}
        />
      ))}
    </div>
  );
}
